import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Mascot } from "@/pages/student/Mascot";
import type { WalkStep } from "./builderWalkthrough";
import "./CourseWalkthrough.css";

export type WalkOutlineEntry = { id: string; title: string; index: number };

type Mode = "offer" | "tour";
type Side = "left" | "right" | "above" | "below";

type Hole = { top: number; left: number; width: number; height: number };
type Anchor = { top: number; left: number; side: Side };

const PAD = 8;
const GAP = 16;
const MARGIN = 12;

function clamp(n: number, min: number, max: number) {
  return Math.min(Math.max(n, min), max);
}

function chooseAnchor(target: DOMRect, width: number, height: number): Anchor {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const options: Anchor[] = [
    {
      side: "right",
      top: target.top + target.height / 2 - height / 2,
      left: target.right + GAP,
    },
    {
      side: "left",
      top: target.top + target.height / 2 - height / 2,
      left: target.left - GAP - width,
    },
    {
      side: "below",
      top: target.bottom + GAP,
      left: Math.min(target.left, vw - width - MARGIN),
    },
    {
      side: "above",
      top: target.top - GAP - height,
      left: Math.min(target.left, vw - width - MARGIN),
    },
  ];
  const fits = (spot: Anchor) =>
    spot.left >= MARGIN &&
    spot.top >= MARGIN &&
    spot.left + width <= vw - MARGIN &&
    spot.top + height <= vh - MARGIN;
  const chosen = options.find(fits) ?? options[2];
  return {
    side: chosen.side,
    top: clamp(chosen.top, MARGIN, Math.max(MARGIN, vh - height - MARGIN)),
    left: clamp(chosen.left, MARGIN, Math.max(MARGIN, vw - width - MARGIN)),
  };
}

function ensureVisible(el: HTMLElement) {
  const scroller = el.closest(".al-shell-body");
  const view =
    scroller instanceof HTMLElement
      ? scroller.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth };
  const rect = el.getBoundingClientRect();
  const room = 28;
  const inView =
    rect.top >= view.top + room &&
    rect.bottom <= view.bottom - room &&
    rect.left >= view.left &&
    rect.right <= view.right;
  if (!inView) el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
}

function visibleTourTarget(step: WalkStep): HTMLElement | null {
  const names = [step.target, ...(step.fallback ?? [])];
  for (const name of names) {
    const nodes = document.querySelectorAll(`[data-tour="${CSS.escape(name)}"]`);
    for (const node of nodes) {
      if (!(node instanceof HTMLElement)) continue;
      const rect = node.getBoundingClientRect();
      if (rect.width >= 2 && rect.height >= 2) return node;
    }
  }
  return null;
}

function focusControl(el: HTMLElement) {
  const node =
    el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
      ? el
      : el.querySelector("input, textarea");
  if (node instanceof HTMLElement) node.focus({ preventScroll: true });
}

export function CourseWalkthrough({
  mode,
  steps,
  stepIndex,
  chapter,
  outline,
  onJump,
  onStart,
  onDismiss,
  onClose,
  onBack,
  onNext,
}: {
  mode: Mode;
  steps: WalkStep[];
  stepIndex: number;
  chapter: string;
  outline: WalkOutlineEntry[];
  onJump: (index: number) => void;
  onStart: () => void;
  onDismiss: () => void;
  onClose: () => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const step = steps[Math.min(stepIndex, Math.max(steps.length - 1, 0))];
  const speakerRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [hole, setHole] = useState<Hole | null>(null);
  const [anchor, setAnchor] = useState<Anchor>({ top: MARGIN, left: MARGIN, side: "right" });
  const [ready, setReady] = useState(false);
  const [outlineOpen, setOutlineOpen] = useState(true);

  useEffect(() => {
    if (mode !== "tour" || !step) return undefined;
    let alive = true;
    let frames = 0;
    let raf = 0;
    const look = () => {
      if (!alive) return;
      const el = visibleTourTarget(step);
      if (!el) {
        if (frames < 40) {
          frames += 1;
          raf = window.requestAnimationFrame(look);
        }
        return;
      }
      ensureVisible(el);
      if (step.focus) focusControl(el);
      else nextRef.current?.focus({ preventScroll: true });
    };
    look();
    return () => {
      alive = false;
      window.cancelAnimationFrame(raf);
    };
  }, [mode, step]);

  useLayoutEffect(() => {
    if (mode !== "tour" || !step) return undefined;
    let frames = 0;
    let raf = 0;
    let focused: HTMLElement | null = null;
    let watched: HTMLElement | null = null;
    let observer: ResizeObserver | null = null;

    const measure = () => {
      const speaker = speakerRef.current;
      const el = visibleTourTarget(step);
      if (!speaker) return;
      const rect = el instanceof HTMLElement ? el.getBoundingClientRect() : null;
      if (!(el instanceof HTMLElement) || !rect || rect.width < 2 || rect.height < 2) {
        if (frames < 40) {
          frames += 1;
          raf = window.requestAnimationFrame(measure);
          return;
        }
        const box = speaker.getBoundingClientRect();
        setHole(null);
        setAnchor({
          side: "below",
          top: Math.max(MARGIN, window.innerHeight / 2 - box.height / 2),
          left: Math.max(MARGIN, window.innerWidth / 2 - box.width / 2),
        });
        setReady(true);
        return;
      }
      if (watched !== el && typeof ResizeObserver !== "undefined") {
        observer?.disconnect();
        observer = new ResizeObserver(measure);
        observer.observe(el);
        watched = el;
      }
      if (focused !== el) {
        focused = el;
        ensureVisible(el);
        if (step.focus) focusControl(el);
      }
      const box = speaker.getBoundingClientRect();
      setHole({
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      });
      setAnchor(chooseAnchor(rect, box.width, box.height));
      setReady(true);
    };

    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    let scheduled = 0;
    const onTree = () => {
      if (scheduled) return;
      scheduled = window.requestAnimationFrame(() => {
        scheduled = 0;
        measure();
      });
    };
    const tree = new MutationObserver(onTree);
    tree.observe(document.body, { childList: true, subtree: true });
    return () => {
      window.cancelAnimationFrame(raf);
      window.cancelAnimationFrame(scheduled);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      observer?.disconnect();
      tree.disconnect();
    };
  }, [mode, step, outlineOpen]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (mode === "offer") onDismiss();
        else onClose();
        return;
      }
      if (mode !== "tour") return;
      const node = event.target as HTMLElement | null;
      const tag = node?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || node?.isContentEditable) return;
      if (node?.closest(".al-cb-tour")) return;
      if (event.key === "ArrowRight" || event.key === "Enter") {
        event.preventDefault();
        onNext();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        onBack();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, onBack, onClose, onDismiss, onNext]);

  if (mode === "offer") {
    return createPortal(
      <div className="al-cb-tour al-cb-tour--offer" role="dialog" aria-modal="true" aria-labelledby="cb-tour-offer-text">
        <div className="al-cb-tour-offer">
          <Mascot pose="waving" size={128} />
          <div className="al-cb-tour-bubble">
            <p className="al-cb-tour-who">Butler</p>
            <p id="cb-tour-offer-text" className="al-cb-tour-text">
              Would you like help creating a course?
            </p>
          </div>
          <div className="al-cb-tour-actions">
            <button type="button" className="al-cb-btn al-cb-btn--primary" autoFocus onClick={onStart}>
              Start Walkthrough
            </button>
            <button type="button" className="al-cb-btn al-cb-btn--ghost" onClick={onDismiss}>
              Not now
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  if (!step) return null;

  const last = stepIndex >= steps.length - 1;
  const blocks = hole
    ? [
        { top: 0, left: 0, width: "100%", height: hole.top },
        { top: hole.top, left: 0, width: hole.left, height: hole.height },
        { top: hole.top, left: hole.left + hole.width, width: `calc(100% - ${hole.left + hole.width}px)`, height: hole.height },
        { top: hole.top + hole.height, left: 0, width: "100%", height: `calc(100% - ${hole.top + hole.height}px)` },
      ]
    : [{ top: 0, left: 0, width: "100%", height: "100%" }];

  return createPortal(
    <div className="al-cb-tour" role="presentation">
      {blocks.map((block, index) => (
        <div
          key={index}
          className="al-cb-tour-block"
          style={{ top: block.top, left: block.left, width: block.width, height: block.height }}
        />
      ))}
      {hole ? (
        <div
          className="al-cb-tour-ring"
          style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
        />
      ) : null}
      <div
        ref={speakerRef}
        className="al-cb-tour-speaker"
        data-side={anchor.side}
        role="dialog"
        aria-modal="false"
        aria-labelledby="cb-tour-step-text"
        style={{
          top: anchor.top,
          left: anchor.left,
          visibility: ready ? "visible" : "hidden",
        }}
      >
        <Mascot pose={step.pose} size={96} />
        <div className="al-cb-tour-bubble">
          <div className="al-cb-tour-bubble-head">
            <p className="al-cb-tour-who">Butler</p>
            <button type="button" className="al-cb-tour-end" aria-label="End walkthrough" onClick={onClose}>
              <X size={14} />
            </button>
          </div>
          <p className="al-cb-tour-kicker">
            {chapter} · {stepIndex + 1} of {steps.length}
          </p>
          <p id="cb-tour-step-text" className="al-cb-tour-text">
            {step.text}
          </p>
          {outline.length > 1 ? (
            <div className="al-cb-tour-outline">
              <button
                type="button"
                className="al-cb-tour-outline-toggle"
                aria-expanded={outlineOpen}
                onClick={() => setOutlineOpen((open) => !open)}
              >
                Outline
              </button>
              {outlineOpen ? (
                <nav className="al-cb-tour-outline-list" aria-label="Walkthrough outline">
                  {outline.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      className={entry.title === chapter ? "is-on" : ""}
                      aria-current={entry.title === chapter ? "step" : undefined}
                      onClick={() => onJump(entry.index)}
                    >
                      {entry.title}
                    </button>
                  ))}
                </nav>
              ) : null}
            </div>
          ) : null}
          <div className="al-cb-tour-actions">
            <button type="button" className="al-cb-btn al-cb-btn--ghost" onClick={onBack} disabled={stepIndex === 0}>
              Back
            </button>
            <button ref={nextRef} type="button" className="al-cb-btn al-cb-btn--primary" onClick={onNext}>
              {last ? "Done" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
